import { useEffect, useMemo, useState } from 'react';
import { fetchOperationPolicy } from '@/api/shops';
import { fetchWeeklyOpenSlots } from '@/api/openSlots';
import { buildSlotsMapFromOpenSlots } from '@/utils/schedule';
buildSlotsMapFromOpenSlots
export function minuteToTime(min) {
  const hour = Math.floor(min / 60)
    .toString()
    .padStart(2, '0');
  const minute = (min % 60).toString().padStart(2, '0');
  return `${hour}:${minute}`;
}

export function dateToString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getWeekStart(date) {
  const next = new Date(date);
  const day = next.getDay();
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() - day);
  return next;
}

export function getWeekDates(weekStart) {
  return Array.from({ length: 7 }, (_, i) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + i);
    return next;
  });
}

export function cloneSlotsMap(slots) {
  const result = {};
  Object.keys(slots || {}).forEach((slotDate) => {
    result[slotDate] = {};
    Object.keys(slots[slotDate] || {}).forEach((roomId) => {
      result[slotDate][roomId] = [...slots[slotDate][roomId]];
    });
  });
  return result;
}

export function normalizeMinuteArray(minutes) {
  return [...new Set((minutes || []).map(Number))].sort((a, b) => a - b);
}

export function slotsAreEqual(a, b) {
  return JSON.stringify(a || {}) === JSON.stringify(b || {});
}

export function buildWeeklyPayload(weekDates, slots) {
  const startDate = dateToString(weekDates[0]);
  const endDate = dateToString(weekDates[6]);

  const weekSlots = {};

  weekDates.forEach((date) => {
    const slotDate = dateToString(date);
    const dateSlots = slots[slotDate];

    if (!dateSlots) return;

    const roomMap = {};

    Object.keys(dateSlots).forEach((roomId) => {
      const normalized = normalizeMinuteArray(dateSlots[roomId]);
      if (normalized.length > 0) {
        roomMap[String(roomId)] = normalized;
      }
    });

    if (Object.keys(roomMap).length > 0) {
      weekSlots[slotDate] = roomMap;
    }
  });

  return {
    startDate,
    endDate,
    slots: weekSlots,
  };
}

export function toggleSlotValue({prev, slotDate, roomId, minute, shouldOpen, toggleSlot = false}) {
  const next = cloneSlotsMap(prev);
  const roomKey = String(roomId);

  if (!next[slotDate]) {
    next[slotDate] = {};
  }

  if (!next[slotDate][roomKey]) {
    next[slotDate][roomKey] = [];
  }

  const current = new Set(next[slotDate][roomKey]);
  if (toggleSlot) {
    if (current.has(minute)) {
      current.delete(minute)
    } else {
      current.add(minute)
    }
  } else if (shouldOpen) {
    current.add(minute);
  } else {
    current.delete(minute);
  }

  const normalized = normalizeMinuteArray([...current]);

  if (normalized.length === 0) {
    delete next[slotDate][roomKey];
    if (Object.keys(next[slotDate]).length === 0) {
      delete next[slotDate];
    }
  } else {
    next[slotDate][roomKey] = normalized;
  }

  return next;
}

export function normalizeBlocks(blocks = []) {
  return [...blocks]
    .map((block) => ({
      startMinuteOfDay: Number(block.startMinuteOfDay),
      endMinuteOfDay: Number(block.endMinuteOfDay),
    }))
    .filter(
      (block) =>
        !Number.isNaN(block.startMinuteOfDay) &&
        !Number.isNaN(block.endMinuteOfDay) &&
        block.startMinuteOfDay < block.endMinuteOfDay
    )
    .sort((a, b) => a.startMinuteOfDay - b.startMinuteOfDay);
}

export function buildOperatingHourSettingsFromLegacy(operatingHours = []) {
  const activeRows = operatingHours
    .filter((item) => Number(item.activeYn) === 1)
    .sort((a, b) => Number(a.weekday) - Number(b.weekday));

  const firstActive = activeRows[0];

  const defaultBlocks =
    firstActive && firstActive.openMinuteOfDay != null && firstActive.closeMinuteOfDay != null
      ? normalizeBlocks(
          firstActive.lunchStartMinuteOfDay != null && firstActive.lunchEndMinuteOfDay != null
            ? [
                {
                  startMinuteOfDay: Number(firstActive.openMinuteOfDay),
                  endMinuteOfDay: Number(firstActive.lunchStartMinuteOfDay),
                },
                {
                  startMinuteOfDay: Number(firstActive.lunchEndMinuteOfDay),
                  endMinuteOfDay: Number(firstActive.closeMinuteOfDay),
                },
              ]
            : [
                {
                  startMinuteOfDay: Number(firstActive.openMinuteOfDay),
                  endMinuteOfDay: Number(firstActive.closeMinuteOfDay),
                },
              ]
        )
      : [];

  const weekdays = Array.from({ length: 7 }, (_, weekday) => {
    const row = operatingHours.find((item) => Number(item.weekday) === weekday);

    if (!row || Number(row.activeYn) !== 1) {
      return {
        weekday,
        useCustomYn: 0,
        isClosedYn: 1,
        blocks: [],
      };
    }

    const rowBlocks = normalizeBlocks(
      row.lunchStartMinuteOfDay != null && row.lunchEndMinuteOfDay != null
        ? [
            {
              startMinuteOfDay: Number(row.openMinuteOfDay),
              endMinuteOfDay: Number(row.lunchStartMinuteOfDay),
            },
            {
              startMinuteOfDay: Number(row.lunchEndMinuteOfDay),
              endMinuteOfDay: Number(row.closeMinuteOfDay),
            },
          ]
        : [
            {
              startMinuteOfDay: Number(row.openMinuteOfDay),
              endMinuteOfDay: Number(row.closeMinuteOfDay),
            },
          ]
    );

    const useCustomYn =
      JSON.stringify(rowBlocks) === JSON.stringify(defaultBlocks) ? 0 : 1;

    return {
      weekday,
      useCustomYn,
      isClosedYn: 0,
      blocks: useCustomYn ? rowBlocks : [],
    };
  });

  return {
    defaultBlocks,
    weekdays,
  };
}

export function normalizeOperationPolicyResponse(raw) {
  const payload = raw?.data ?? raw ?? {};

  if (payload.operatingHourSettings) {
    return {
      bookingPolicy: payload.bookingPolicy || null,
      rooms: payload.rooms || [],
      operatingHourSettings: {
        defaultBlocks: normalizeBlocks(payload.operatingHourSettings.defaultBlocks || []),
        weekdays: (payload.operatingHourSettings.weekdays || []).map((item) => ({
          weekday: Number(item.weekday),
          useCustomYn: Number(item.useCustomYn || 0),
          isClosedYn: Number(item.isClosedYn || 0),
          blocks: normalizeBlocks(item.blocks || []),
        })),
      },
    };
  }

  return {
    bookingPolicy: payload.bookingPolicy || null,
    rooms: payload.rooms || [],
    operatingHourSettings: buildOperatingHourSettingsFromLegacy(payload.operatingHours || []),
  };
}

export function getWeekdayRule(operatingHourSettings, weekday) {
  return (operatingHourSettings?.weekdays || []).find(
    (item) => Number(item.weekday) === Number(weekday)
  );
}

export function getEffectiveBlocksForWeekday(operatingHourSettings, weekday) {
  const rule = getWeekdayRule(operatingHourSettings, weekday);
  const defaultBlocks = normalizeBlocks(operatingHourSettings?.defaultBlocks || []);

  if (!rule) return defaultBlocks;
  if (Number(rule.isClosedYn) === 1) return [];
  if (Number(rule.useCustomYn) === 1) return normalizeBlocks(rule.blocks || []);
  return defaultBlocks;
}

export function formatBlocks(blocks = []) {
  if (!blocks.length) return '휴무';
  return blocks
    .map((block) => `${minuteToTime(block.startMinuteOfDay)}~${minuteToTime(block.endMinuteOfDay)}`)
    .join(' / ');
}

export function isSlotBlockedByBlocks(blocks, startMinuteOfDay, sessionMinutes) {
  if (!Array.isArray(blocks) || blocks.length === 0) return true;

  const endMinuteOfDay = startMinuteOfDay + sessionMinutes;

  return !blocks.some(
    (block) =>
      startMinuteOfDay >= Number(block.startMinuteOfDay) &&
      endMinuteOfDay <= Number(block.endMinuteOfDay)
  );
}

export function getVisibleTimeAxisFromBlocks(weekDates, operatingHourSettings, sessionMinutes) {
  let minMinute = Infinity;
  let maxMinute = -Infinity;

  weekDates.forEach((date) => {
    const blocks = getEffectiveBlocksForWeekday(operatingHourSettings, date.getDay());
    blocks.forEach((block) => {
      if (Number(block.startMinuteOfDay) < minMinute) {
        minMinute = Number(block.startMinuteOfDay);
      }
      if (Number(block.endMinuteOfDay) > maxMinute) {
        maxMinute = Number(block.endMinuteOfDay);
      }
    });
  });

  if (minMinute === Infinity || maxMinute === -Infinity || !sessionMinutes) {
    return [];
  }

  const times = [];
  for (let minute = minMinute; minute + sessionMinutes <= maxMinute; minute += sessionMinutes) {
    times.push(minute);
  }
  return times;
}

export function hasOpenSlot(slots, slotDate, roomId, minute) {
  return slots[slotDate]?.[String(roomId)]?.includes(minute) ?? false;
}

export function useWeeklyScheduleBase({
  shopId, 
  initialDate = new Date()
}) {
  const [operationPolicy, setOperationPolicy] = useState({
    bookingPolicy: null,
    rooms: [],
    operatingHourSettings: {
      defaultBlocks: [],
      weekdays: [],
    },
  });

  const [currentWeekStart, setCurrentWeekStart] = useState(() => getWeekStart(initialDate));
  const [roomFilter, setRoomFilter] = useState(null);
  const [openSlotStatusMap, setOpenSlotStatusMap] = useState({});
  const [bookedStatusMap, setBookedStatusMap] = useState({});
  const [isPolicyLoading, setIsPolicyLoading] = useState(true);
  const [isWeekLoading, setIsWeekLoading] = useState(false);

  const sessionMinutes = Number(operationPolicy.bookingPolicy?.sessionMinutes || 0);

  const rooms = useMemo(() => {
    return (operationPolicy.rooms || [])
      .filter((room) => Number(room.activeYn) === 1)
      .sort((a, b) => Number(a.roomSeq) - Number(b.roomSeq));
  }, [operationPolicy.rooms]);

  const weekDates = useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);

  const filteredRooms = useMemo(() => {
    if (!roomFilter) return rooms;
    return rooms.filter((room) => String(room.id) === String(roomFilter));
  }, [rooms, roomFilter]);

  const timeAxis = useMemo(() => {
    return getVisibleTimeAxisFromBlocks(
      weekDates,
      operationPolicy.operatingHourSettings,
      sessionMinutes
    );
  }, [weekDates, operationPolicy.operatingHourSettings, sessionMinutes]);
  async function getOperationPolicy() {
    setIsPolicyLoading(true);
    try {
      if (!shopId) {
        return
      }
      const response = await fetchOperationPolicy(shopId);
      const normalized = normalizeOperationPolicyResponse(response);
      setOperationPolicy(normalized);
    } catch (error) {
      alert(error.message || '운영 정책을 불러오지 못했습니다.');
    } finally {
      setIsPolicyLoading(false);
    }
  }
  useEffect(() => {
    getOperationPolicy();
  }, [shopId]);

  async function loadWeekSlots() {
    if (!operationPolicy.bookingPolicy) return;
    
    
    setIsWeekLoading(true);
    try {
      const startDate = dateToString(weekDates[0]);
      const endDate = dateToString(weekDates[6]);
      const { data: list } = await fetchWeeklyOpenSlots({startDate, endDate, shopId});
      const { openSlotStatusMap, bookedStatusMap } = buildSlotsMapFromOpenSlots(list);
      setOpenSlotStatusMap(openSlotStatusMap || {});
      setBookedStatusMap(bookedStatusMap || {});
    } catch (error) {
      setOpenSlotStatusMap({});
      setBookedStatusMap({});
    } finally {
      setIsWeekLoading(false);
    }
  }
  useEffect(() => {
    loadWeekSlots();
  }, [operationPolicy.bookingPolicy, weekDates]);

  return {
    operationPolicy,
    currentWeekStart,
    setCurrentWeekStart,
    roomFilter,
    setRoomFilter,
    openSlotStatusMap,
    setOpenSlotStatusMap,
    bookedStatusMap,
    setBookedStatusMap,
    isPolicyLoading,
    isWeekLoading,
    sessionMinutes,
    rooms,
    weekDates,
    filteredRooms,
    timeAxis,
  };
}